import { Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fullSize } from '../../../Constants';
import store, { IAppReduxStore } from '../../../redux/store';
import Footer from '../../General/Footer/Footer';
import Header from '../../General/Header/Header';
import ProjectViewControls from './ProjectViewControls';
import ProjectViewError from './ProjectViewError';
import SelectedDocumentsView from './SelectedDocumentsView';
import ProjectViewContent from './ProjectViewContent';
import ProjectAreaWarning from './ProjectAreaWarning';
import {
    fetchDocuments,
    fetchViewData,
    fetchProjectData,
    resetDocSelection,
    getRedirectUrl,
} from '../../../util/ProjectUtils';
import ProjectAreaSelector from './ProjectAreaSelector';
import { getCurrentFieldsOrFieldsSchema } from '../../../redux/project/sliceProjectViewData';
import { getProjectLogOpen } from '../../../redux/general/sliceDatabaseAccess';
import ProjectViewAdvancedSearchResults from './ProjectViewAdvancedSearchResults';
import DialogsWrapper from '../../General/Dialogs/DialogsWrapper/DialogsWrapper';
import { useFilesOpen } from '../../../hooks/useFilesOpen';
import { useAddFiles } from '../../../hooks/useAddFiles';

/**
 * Application view that allows the uer to navigate through a project database.
 */
export default () => {
    /** Allow component to change the URL. */
    const navigate = useNavigate();

    /** Get URL parameters from the react router. */
    const params = useParams();

    /** Deconstruct params. */
    const { replicaId, areaName, viewName, category } = params;

    /** Open Files hook with error handling */
    useFilesOpen();

    /** Add Files hook with error handling */
    useAddFiles();

    /** Access relevant state variables from the redux store. */
    const areasData = useSelector((store: IAppReduxStore) => store.projectAreas.data);
    const areasError = useSelector((store: IAppReduxStore) => store.projectAreas.error);
    const viewsData = useSelector((store: IAppReduxStore) => store.projectViews.data);
    const viewsError = useSelector((store: IAppReduxStore) => store.projectViews.error);
    const viewStructure = useSelector(
        (store: IAppReduxStore) => store.projectViewData.structure.data,
    );
    const viewStructureError = useSelector(
        (store: IAppReduxStore) => store.projectViewData.structure.error,
    );
    const fields = useSelector(getCurrentFieldsOrFieldsSchema('fields'));
    const fieldSchema = useSelector(getCurrentFieldsOrFieldsSchema<string>('schema'));

    const areaSearchText = useSelector(
        (_store: IAppReduxStore) => _store.projectAdvancedSearch.areaSearchText,
    );
    const { data: dynamicDialogsResponseData } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocumentsDynamicSelection,
    );
    const { canAccessDatabase } = useSelector((store: IAppReduxStore) => store.databaseAccess);

    /** State that controls the display of the selected documents view. */
    const [showSelectedDocs, setShowSelectedDocs] = useState<boolean>(false);

    /**
     * Effect that triggers a fetch of all relevant data for the user's project.
     */
    useEffect(() => {
        store.dispatch(getProjectLogOpen({ replicaId }));
    }, [replicaId]);

    useEffect(() => {
        if (canAccessDatabase) {
            fetchProjectData(replicaId);
        }
    }, [replicaId, canAccessDatabase]);

    /**
     * Effect that resets document selection if the user changes project or project area.
     */
    useEffect(() => {
        resetDocSelection(params);
    }, [replicaId, areaName]);

    /**
     * Effect that fetches the documents for the user's current view category.
     */
    useEffect(() => {
        fetchDocuments(params);
    }, [viewStructure, fields, fieldSchema, category]);

    useEffect(() => {
        if (!dynamicDialogsResponseData?.refreshView) return;
        fetchDocuments(params);
    }, [dynamicDialogsResponseData]);

    /**
     * Effect that ensures that the user has a correct URL and fetches all relevant data for the user's current view.
     */
    useEffect(() => {
        if (!viewsData || !areasData || !areaName || !viewName) {
            navigate(getRedirectUrl(replicaId, areaName, viewName));

            return;
        }

        fetchViewData(params);
    }, [viewsData, areaName, viewName, areasData]);

    if (areasError || viewsError || viewStructureError) {
        return <ProjectViewError />;
    }

    return (
        <DialogsWrapper area='project'>
            <Header />
            {showSelectedDocs ? (
                <SelectedDocumentsView close={() => setShowSelectedDocs(false)} />
            ) : areaSearchText ? (
                <ProjectViewAdvancedSearchResults setShowSelectedDocs={setShowSelectedDocs} />
            ) : (
                <>
                    <ProjectAreaWarning />

                    <Stack direction='row' sx={{ ...fullSize, flexGrow: 4, overflow: 'hidden' }}>
                        <ProjectAreaSelector />
                        <Stack direction='column' sx={{ flex: 1, overflow: 'hidden' }}>
                            <ProjectViewControls />
                            <ProjectViewContent setShowSelectedDocs={setShowSelectedDocs} />
                        </Stack>
                    </Stack>
                </>
            )}
            <Footer />
        </DialogsWrapper>
    );
};
